import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { Card } from '../base/Card';
import type { Project } from './Portfolio';

export interface ProjectCardProps extends HTMLAttributes<HTMLDivElement> {
  project: Project;
}

/**
 * ProjectCard Component
 * 
 * Card per singolo progetto con hover effect.
 */
export const ProjectCard = forwardRef<HTMLDivElement, ProjectCardProps>(
  ({ project, className, onClick, ...props }, ref) => {
    return (
      <Card
        ref={ref}
        variant="elevated"
        padding="none"
        hoverable
        className={cn('group overflow-hidden cursor-pointer', className)}
        onClick={onClick}
        {...props}
      >
        {/* Image */}
        <div className="relative overflow-hidden aspect-video">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
          {project.category && (
            <div className="absolute top-4 left-4 px-3 py-1 bg-primary text-primary-content text-sm font-semibold rounded-full">
              {project.category}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-base-content mb-2 group-hover:text-primary transition-colors">
            {project.title}
          </h3>
          <p className="text-base-content/70">
            {project.description}
          </p>

          {/* Tags */}
          {project.tags && project.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {project.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-base-200 text-base-content rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Link */}
          {project.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Vedi progetto
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          )}
        </div>
      </Card>
    );
  }
);

ProjectCard.displayName = 'ProjectCard';

